/**
 * §8 이벤트 모델 — 공통 envelope 와 타입 정의.
 *
 * 서버가 아직 없으므로 이 파일이 클라이언트측 계약의 단일 출처다.
 * 목(src/mock)과 실서버 어댑터(src/lib/socket.ts)가 동일하게 이 타입을 만족한다.
 */

/** §8.1 공통 envelope. */
export interface Envelope<T extends EventType = EventType> {
  v: 1
  type: T
  room: string
  /**
   * 방 전역 단조 증가 — 재생·중복 제거의 단일 축(§8.1).
   * `caption.partial` 은 seq 미부여(§8.2)이므로 null.
   */
  seq: number | null
  utt: string | null
  vis: Visibility
  /** epoch ms */
  ts: number
  data: EventDataMap[T]
}

export type Visibility = 'all' | 'host'

export type EventType =
  | 'caption.partial'
  | 'caption.retract'
  | 'caption.final'
  | 'caption.gap'
  | 'caption.skip'
  | 'caption.delete'
  | 'caption.error'
  | 'coach'
  | 'summary'
  /**
   * 어시스트 응답(DEF-071 1단계) — 호스트가 던진 질의에 대한 답 1건.
   * 응답이 REST 본문이 아니라 **버스로** 오는 이유는 `summary` 와 같다:
   * 호스트의 다른 탭·재접속이 같은 답을 보고, 재생이 대화 순서를 그대로
   * 복원한다. vis=host.
   */
  | 'assist'
  | 'room.status'
  | 'utt.state'
  | 'chat'
  | 'chat.delete'
  /**
   * §8.5 채팅 거부 사유 — **이 소켓에만** 온다(팬아웃 대상이 아니다).
   * seq 도 없다: 나가지 않은 메시지가 재생·기록에 남으면 안 된다.
   * 지금은 표시 이름 중복 하나뿐이다 — 사용자가 **고칠 수 있는** 거부만
   * 문장으로 돌려준다(레이트리밋·크기 초과는 조용히 센다, §10.1).
   */
  | 'chat.error'
  /**
   * 화자 **전환 표시** 소급 — 자막은 화자 계산을 기다리지 않고 표시 없이
   * 먼저 나가고, 임베딩이 끝나면 이 이벤트가 과거 카드에 표시를 붙인다.
   * seq 미부여(현재 상태 신호) — 늦은 입장자는 재생 final 에 구워진 표시를
   * 받으므로 재생이 필요 없다.
   *
   * 정체성(`spk1`…)은 없다: "누구인지" 가 아니라 "여기서 바뀐 것 같다" 만
   * 말한다. 이름 지정 경로가 통째로 사라져 `speaker.update` 도 없다.
   */
  | 'speaker.turn'
  /**
   * 검토 배지 소급(DEF-019) — 비동기 교차 확인이 끝난 뒤 이미 그려진 카드에
   * "확인 필요" 를 붙인다. `speaker.turn` 과 같은 이유로 seq 미부여다:
   * 늦은 입장자는 재생 final 에 **구워진** 사유를 그대로 받는다.
   */
  | 'caption.review'
  /**
   * 장문 기록 문단(TRANS_LONGFORM, server/longform.py) — 라이브 자막들이
   * 창(≤60s)으로 묶여 지연 확정된 회의록 문단 1건. 자막과 **같은 seq 축**
   * 을 쓰므로(§8.5 chat 과 같은 근거) 재생·중복 제거가 그대로 성립하고,
   * 늦은 입장자도 재생만으로 통합 화면을 복원한다. vis=all.
   */
  | 'record.paragraph'
  | 'resync'
  /**
   * A1 — 호스트 `ping` 에 대한 **종단 왕복 증거**. 이 소켓에만 온다
   * (팬아웃 대상이 아니고 seq 도 먹지 않는다).
   *
   * 왜 별도 타입인가: 기존 생존 판정(`lastRecvAt`)은 **무엇이든 받으면**
   * 갱신되는데, 서버는 5초 `room.status` 티커를 방 전체에 쏜다. 즉
   * 다운링크만 살아 있으면 업링크가 죽어도 그 값은 신선하다 — 실측 장애
   * A 의 구조다. pong 만이 "내가 보낸 것이 서버에 닿았다" 를 증명한다.
   */
  | 'pong'
  /**
   * 이전 회의 이어하기(S23) — 화면 시딩용. utt='' (멱등 가드 미등록),
   * display-only(회의록 조립 미참여).
   */
  | 'history.seed'

/** §6.2 출력 스키마 — 번역 필드 선행. */
export interface CaptionFinalData {
  lang: string
  /**
   * 표시용 관측값(S38-2) — `lang`(제어용, 방 설정에서 각인된 값)과 분리된
   * 필드다. 실제로 말해진 언어가 문자체계로 구별될 때만 채워지고, 라틴
   * 문자권끼리(vi·en·fr…)는 구별 못 해 빈 문자열로 온다 — 빈 값은
   * "모름"이지 "없음"이 아니다. 화면은 빈 값이면 원어 코드를 찍지 않는다.
   */
  detected_lang?: string
  /** 원문 전사 — '들린 내용' */
  text: string
  /**
   * 타깃 언어 번역 (방향 상태가 의미를 결정 — §6.2).
   *
   * ⚠️ **이름이 `ko` 지만 내용이 한국어라는 뜻이 아니다.** 방의 `dst` 가
   * 무엇이든 이 칸이 번역문이다 — ko→vi 방에서는 여기에 베트남어가 온다.
   *
   * 이름이 남은 이유는 하위 호환이다(2026-08-06 타깃 개방). 서버는 모델에게
   * 보내는 스키마 필드명만 타깃 중립(`tgt`)으로 바꿨고, **전송·저장 계약은
   * `ko` 그대로** 두었다 — 이미 디스크에 쌓인 회의록(`stream.db`·기록 jsonl)과
   * 내보내기 경로가 이 이름으로 읽기 때문이다. 화면이 쓸 값의 의미는
   * "번역문" 이지 "한국어" 가 아니다.
   */
  ko: string
  conf: number
  /** 재확인 사유. 빈 문자열 = 정상 */
  flag: string
  /** 20s 강제 절단으로 분할된 발화(§5) */
  cut: boolean
  attempt: number
  /** 발화 종료 시각(epoch ms) */
  ts_utt: number
  /**
   * §6.5 **서버 후검증의 판정**. 자가보고 conf/flag 만 보고 화면이 다시
   * 판정하면 안 된다: 스크립트 비율·숫자 보존·상투구·2열 붕괴처럼 클라이언트가
   * 알 수 없는 근거로 서버가 세운 깃발을 화면이 조용히 무시하게 된다.
   */
  needs_review?: boolean
  /** 확인 필요 사유(쉼표 구분) — 배지 툴팁에 쓴다 */
  review_reason?: string
  /**
   * §6.5-7 **추론 표시** — `needs_review` 와 **다른 축**이다.
   *
   * `needs_review`("확인 필요")는 "이 자막을 사람이 확인하라" 는 행동 요구다.
   * `inferred`("추론됨")는 **내용의 성질**을 말한다: 이 문장은 회의에서 오간
   * 말이 아니라 모델이 지어낸 것으로 보인다. 사용자가 요구한 것은 행동이
   * 아니라 **구분**이었다("정확하지 않은 통역이 그렇지 않은 통역과 구분이
   * 되지 않게 나와").
   *
   * 둘은 겹칠 수 있으므로 한 키로 합치지 않는다 — 합치면 잘린 발화가 동시에
   * 날조일 때 한쪽 표시가 사라진다.
   */
  inferred?: boolean
  /** 추론 표시 사유(쉼표 구분) — 배지 툴팁에 쓴다 */
  inferred_reason?: string
  /** L2 병합으로 이 발화에 계승된 원 발화 id 들(§7.3) */
  merged_from?: string[]
  /**
   * §6.5 passthrough — **타깃 언어 발화를 들린 그대로** 실은 카드.
   * 통역이 아니므로 원문/번역 2열이 아니라 단일 열로 그리고, 방향 배지도
   * "vi → ko" 가 아니라 발화 언어 코드 하나만 보여 준다(통역으로 오독 방지).
   */
  passthrough?: boolean
  /**
   * 화자 **전환 표시** — **서버 임베딩 사이드카의 후처리 산출물**이다(모델
   * 자가보고가 아니다). 자막은 이 키 **없이** 먼저 오고, 계산이 끝나면
   * `speaker.turn` 이 소급한다.
   *   · `true`      — 직전 발화와 다른 목소리(구분선을 그린다)
   *   · `false`     — 같은 목소리(아무것도 그리지 않는다)
   *   · 키 없음     — 판정하지 않았다(짧은 발화·무음·첫 발화·기능 off)
   * `false` 와 "키 없음" 을 구분하는 이유: 전자는 판정 결과이고 후자는
   * 모른다는 뜻이다. 화면 표시는 같지만 의미가 달라 섞으면 안 된다.
   */
  spk_turn?: boolean
  text_raw?: string
  text_raw_truncated?: boolean
  /**
   * DEF-089 B안 — **2차 귀가 들은 원문**. `text`(1차 전사)를 대체하지 않고
   * 그 아래에 한 줄 더 붙는다: 1차가 지어낸 경우 사용자가 두 원문을 나란히
   * 보고 판단한다.
   *
   * 세 키(`text_alt`·`text_alt_source`·`text_alt_hint`)는 **한 덩어리**로
   * 오거나 셋 다 없다 — 조합을 다룰 필요가 없다. 키 부재 = "이 줄을 그리지
   * 마라"(`text_raw` 와 같은 계약).
   */
  text_alt?: string
  /**
   * 2차 귀 슬롯의 **표시 이름**(서버 설정값 그대로). 빈 문자열은 "이름을
   * 정하지 않았다" 는 뜻이지 "출처가 없다" 가 아니다 — 화면은 빈 값이면
   * 출처를 쓰지 않고 중립 문구만 쓴다(`detected_lang` 과 같은 규율).
   */
  text_alt_source?: string
  /**
   * 서버가 계산한 "펼쳐 볼 가치가 있다" 표시. **배지가 아니다** —
   * `needs_review`/`inferred` 와 다른 축이고, 펼치기 어포던스 문구만 바꾼다.
   * 화면이 문턱을 다시 계산하면 안 된다(서버가 문턱을 바꿔도 구 클라이언트가
   * 옛 문턱으로 그린다).
   */
  text_alt_hint?: boolean
}

export interface CaptionPartialData {
  utt: string
  ko: string
}

export interface CaptionRetractData {
  utt: string
}

export type GapReason =
  | 'drop'
  | 'client_gap'
  /**
   * B1 — 모델이 `skip=true` 라 했지만 **서버 증거가 반박한** 구간.
   * 길이·RMS 가 정상 대화 범위인데 되물어도 대상 외라고 하면, 진짜 발언이
   * 증발했을 가능성이 높다. 뷰어에게 빈 자리를 남기지 않는다(§1 원칙 3) —
   * `caption.skip` 은 vis=host 라 참가자 화면에 닿지 않기 때문에 갭으로
   * 표면화한다.
   */
  | 'skip_unverified'
  /**
   * verify 가 오염(반복·페르소나 등)으로 판정해 버린 구간. L3 적체 드롭
   * ('drop')과 원인이 다르므로 화면 라벨을 분리한다(S24).
   */
  | 'verify_drop'
  /**
   * 전사 시점 게이트가 통역 대상 아님(suspect/NOT_SPEECH)으로 판정해
   * 표면화한 구간
   */
  | 'gate_rejected'

export interface CaptionGapData {
  /** 유실 구간 시작(epoch ms). 서버 드롭은 발화 경계, client_gap 은 실측 시각 */
  from_ts: number
  to_ts: number
  reason: GapReason
  /**
   * client_gap 한정 — 서버가 seq 갭에서 센 유실 프레임 수와 그 근사 길이(ms).
   * 서버 드롭(reason='drop')에는 없다: 그쪽은 발화 단위라 프레임 개념이 없다.
   */
  frames_lost?: number
  approx_ms?: number
  /**
   * D3 — client_gap 한정 **로컬 전용** 사유. 서버는 이 키를 절대 보내지
   * 않는다(`caption.gap` 서버 규약 불변) — 호스트 화면이 스스로 dispatch 한
   * 갭에만 실린다.
   *
   * `reason` 에 접어 넣지 않는 이유: 리듀서의 `mergeGapInto` 가 사유가
   * 다르면 병합을 거부하므로 적체/축출이 교대할 때 갭 카드가 벽처럼 쌓이고,
   * 갭 카드 본문 문구 분기도 바뀐다.
   */
  client_reason?: 'backlog' | 'stall_evict'
}

export interface CaptionSkipData {
  lang: string
  /** 참고 표시용 원문(호스트 전용) */
  text: string
}

export interface CaptionDeleteData {
  target_seq: number | null
  target_utt: string | null
}

export interface CaptionErrorData {
  reason: string
  utt: string | null
}

/** §8.5 채팅 거부 사유 — 화면이 **그대로** 보여 준다(서버 문구가 정본). */
export interface ChatErrorData {
  message: string
}

export type CoachKind = '제안' | '신호' | '요지' | '불일치' | '미답'

export interface CoachData {
  kind: CoachKind
  body: string
  ref_seq: number[]
}

/**
 * 어시스트 응답 — 서버 `assist` data 계약(server/assist.py·main.py 정본).
 */
export interface AssistData {
  /** 호스트가 던진 질문(에코 — 화면이 말풍선 짝을 맞춘다) */
  q: string
  /** 답변 본문 */
  body: string
  /**
   * `false` 면 **모델을 부르지 않았다**(문맥 공백). 서버가 먼저 찾고 모델은
   * 답만 쓴다 — 실린 것이 없으면 "없다" 는 사실 자체가 답이고, 그 자리를
   * 모델에게 넘기면 지어낸다(DEF-071 §D 날조 금지).
   *
   * 축은 **문맥 공백**이지 검색 0건이 아니다(DEF-074): 광의 질의("지금까지
   * 요약")는 원래 `refs: []` 이면서 문단 전체가 문맥에 실린다 — 거기서
   * 모델을 안 부르면 요약형 질의가 통째로 깨진다.
   */
  found: boolean
  /**
   * 문맥이 어디서 왔나. `'captions_only'` 면 그 방은 긴호흡이 꺼져 있어
   * 문단이 **영원히** 0건이다 — 화면이 열화 배너를 띄운다(기능이 죽는 게
   * 아니라 열화된다고 말한다).
   */
  context_source: 'paragraphs' | 'paragraphs+captions' | 'captions_only'
  /** 예산 초과로 검색 히트만 실었나 — 조용한 축소 금지 */
  truncated: boolean
  /** 근거 — 누르면 해당 `utt` 로 점프한다 */
  refs: AssistRef[]
  /**
   * 질문 주체 지문(D3-2). `""` = 호스트 질의. 뷰어 질의는 서버가 이 값으로
   * "본인 것만" 을 복원하므로 화면은 읽기만 한다(판정은 서버에 있다).
   */
  subject: string
  /** 표시용 이름(D3-2, `room.pin_chat_name` 규율 재사용). `""` 면 미표시 */
  asker: string
}

export interface AssistRef {
  /** 문단 창 시작(epoch ms) */
  ts: number
  /** 점프 대상 발화 id (없으면 빈 문자열) */
  utt: string
  /** 화면에 그릴 짧은 발췌 */
  excerpt: string
  /** 전사 신뢰도가 낮은 대목 — 화면이 "불확실" 을 표시한다 */
  uncertain: boolean
}

export interface SummaryData {
  /** 마크다운 */
  body: string
  /**
   * 서버는 요약과 의도 해석을 **같은 엔드포인트·같은 이벤트**로 낸다
   * (`POST /api/rooms/{code}/analyze` → `summary{body}`). 화면이 두 종류를
   * 구분해 표시하고 싶어도 와이어에 근거가 없으므로 옵셔널로 둔다 —
   * 없으면 '요약' 으로 표시한다. 서버가 구분을 싣기 시작하면 그때 채워진다.
   */
  kind?: 'summary' | 'intent'
}

/** §9 방 상태 머신 + §7.3 적체 단계. */
export type RoomState =
  | 'open'
  | 'live'
  | 'suspended'
  | 'closed'
  | 'degraded'
  | 'model_down'

/**
 * §8.2 `room.status` — **정본 형태**(서버 `Room.status_data` 와 1:1).
 *
 * 정본 규약: `state` 는 소문자 열거값 · `level` 은 숫자 0-3 · 시간 필드는
 * 전부 `_ms` 접미의 정수. (대문자 state 와 "L1" 문자열, 초 단위 p50/p95 가
 * 섞여 있던 것을 양측에서 하나로 맞췄다.)
 */
export interface RoomStatusData {
  state: RoomState
  viewers: number
  lag_ms: number
  /** §7.3 적체 단계 — 0(정상)/1(경고)/2(병합)/3(드롭) */
  level?: 0 | 1 | 2 | 3
  /** §8.2 r3 — p50_ms/p95_ms 는 host 전용 확장 필드(뷰어에 오지 않는다) */
  p50_ms?: number
  p95_ms?: number
  /**
   * 게이트웨이 안내(TASK_agw_client §3, host 전용) — 최근 30s 안에 상류가
   * 429(busy)/503(down)/504·예산 초과(timeout)를 돌려줬다. 회로가 열리면
   * `state: 'model_down'` 이 우선이라 그때는 오지 않는다. 없으면 정상.
   */
  upstream_notice?: UpstreamNotice
}

export type UpstreamNotice = 'busy' | 'down' | 'timeout'

/** 화자 전환 표시 소급 — 서버 임베딩이 끝난 뒤 과거 카드에 표시를 붙인다. */
export interface SpeakerTurnData {
  utt: string
  /** 직전 발화와 화자가 바뀌었나. 모름은 이벤트 자체가 오지 않는다. */
  changed: boolean
}

/**
 * 검토 배지 소급(DEF-019) — 비동기 교차 확인이 끝난 뒤 **이미 그려진** 카드에
 * "확인 필요" 를 붙인다. `speaker.turn` 과 같은 부류(무시퀀스 소급)다.
 *
 * 자막 본문은 바뀌지 않는다: 이 이벤트가 채우는 것은 `needs_review` 와
 * `review_reason` 뿐이고, 사유는 기존 사유에 **병합**된다(덮어쓰면 먼저 붙은
 * `gate_suspect` 가 조용히 사라진다).
 */
export interface CaptionReviewData {
  utt: string
  review_reason: string
}

/**
 * 장문 기록 문단 — 서버 `record.paragraph` data 계약(events.py 정본).
 * 화면은 `utt_ids` 로 자기 피드의 라이브 카드들을 이 문단으로 통합한다.
 */
export interface RecordParagraphData {
  /** 창 시작(epoch ms) — envelope ts(방출 시각)와 다르다 */
  ts: number
  /** 창 길이(초) */
  dur_s: number
  /** 창의 원어(말해진 언어) */
  lang: string
  /** 복원·번역된 문단(항상 한국어 — RECORD_SYSTEM 계약) */
  ko: string
  /** 이 문단이 덮는 발화 id 들 — 라이브 카드 통합의 축 */
  utt_ids: string[]
  /** 복원 모델 자가보고 — true 면 문단에 확인 필요 배지 */
  uncertain: boolean
}

/** §8.3 서버가 지시하는 재동기화. */
export interface ResyncData {
  /** 재재생이 시작되는 seq — 이보다 이전은 유실 구간 */
  from_seq: number
  /**
   * `window_exceeded` — 재접속 커서가 링버퍼 창 밖(§8.3).
   * `slow_consumer` — 뷰어 큐가 상한(512)을 넘겨 강제 재동기화(§8.3).
   */
  reason: 'window_exceeded' | 'slow_consumer'
}

/**
 * §14 발화 수명주기(호스트 전용) — "듣는중 / 통역 중" 표시의 근거.
 *
 * VAD 는 서버에 있어서 화면이 "지금 발화를 잡고 있는지" 를 알 방법이 없다.
 * 로컬 마이크 레벨로 추측하면 서버의 실제 분절 판정과 어긋난다.
 */
export interface UttStateData {
  utt: string
  /** open=발화 포착 중 · inflight=모델 추론 중 · done=닫힘 */
  state: 'open' | 'inflight' | 'done'
  /** 방의 현재 원언어(방향 전환을 따라간다) */
  src: string
  /**
   * 서버가 실측한 진행 관측치(ms) — open 은 지금까지 잡힌 오디오 길이,
   * inflight 는 통역에 들어간 발화 분량. **서버 정본**이다: 프레임이 실제로
   * 도착해야만 자라는 값이라, 입력이 죽으면 이 숫자도 함께 멎는다(§14 —
   * 지어낸 진행 표시 금지). 구 서버는 안 싣는다 — 없으면 그리지 않는다.
   */
  dur_ms?: number
  /**
   * 마지막 프레임의 **서버 실측** 음량(dBFS, 대략 −60…0).
   * 카드의 파형 애니메이션이 이 값으로 움직인다 — 클라이언트가 자체 계산한
   * 레벨을 쓰면 브라우저가 무음을 보내는 동안에도 미터가 춤춘다(그렇게
   * 회의 하나가 통째로 날아갔다). 구 서버는 안 싣는다 — 없으면 안 그린다.
   */
  level_dbfs?: number
}

/**
 * §8.5 회의 중 채팅 — 자막과 **같은 seq 축**을 쓴다.
 * 재생·중복 제거·삭제가 자막과 동일한 규율을 그대로 따른다.
 */
export interface ChatData {
  body: string
  /**
   * 표시 이름 — 첫 메시지와 함께 받는다(§14.2).
   * **신뢰 신호가 아니다**: 참가자가 자유롭게 정하므로 "호스트" 라고 쓸 수
   * 있다. 역할 표시는 반드시 아래 `role` 로만 그린다.
   */
  name: string
  /** 계정 사용자면 계정명(감사 축, §11). 익명이면 빈 문자열 */
  author: string
  anonymous: boolean
  /**
   * **서버가 판정한 역할**(§8.5) — 화면의 신뢰 배지는 이 값만 본다.
   * 클라이언트가 주입해도 서버가 덮어쓰므로 위조가 불가능하다.
   * 구 서버 호환을 위해 옵셔널(없으면 viewer 로 본다).
   */
  role?: 'host' | 'viewer'
}

export interface ChatDeleteData {
  target_seq: number
}

/**
 * 이전 회의 이어하기(S23) — 새 방 개설 시 아카이브에서 시딩된 화면 이벤트.
 * caption 또는 paragraph 아카이브 행 1건에 대응한다. utt=""(멱등 가드
 * 미등록) 이고 seq 는 서버가 저장된 값 그대로 되살린다(restore, 재발행 아님).
 */
export interface HistorySeedData {
  /** 원본 행의 종류 — caption 은 자막 finals, paragraph 는 장문 문단 */
  kind: 'caption' | 'paragraph'
  /** 원본 발화의 원어 코드 */
  lang: string
  /** 원문 전사 — paragraph 행은 항상 빈 문자열(카드 흡수 로직 미발동) */
  text: string
  /** 통역/복원 자막(한국어) */
  ko: string
  /** 원본 발화·문단 시각(epoch ms) */
  ts: number
  /** 복원 모델 자가보고 불확실 표시 (paragraph 전용, caption 은 항상 false) */
  uncertain: boolean
  /** 이 시드가 온 이전 회의 코드 */
  prev_room: string
}

export interface EventDataMap {
  'caption.partial': CaptionPartialData
  'caption.retract': CaptionRetractData
  'caption.final': CaptionFinalData
  'caption.gap': CaptionGapData
  'caption.skip': CaptionSkipData
  'caption.delete': CaptionDeleteData
  'caption.error': CaptionErrorData
  coach: CoachData
  summary: SummaryData
  assist: AssistData
  'room.status': RoomStatusData
  'utt.state': UttStateData
  'speaker.turn': SpeakerTurnData
  'caption.review': CaptionReviewData
  'record.paragraph': RecordParagraphData
  chat: ChatData
  'chat.delete': ChatDeleteData
  'chat.error': ChatErrorData
  resync: ResyncData
  pong: PongData
  'history.seed': HistorySeedData
}

/**
 * A1 pong — 서버가 **실제로 받은 것**을 되돌려 준다.
 *
 * 이 셋이 셋 다 필요한 이유가 다르다:
 *  · `ack` 정체 = 소켓은 살아 있는데 오디오만 안 올라온다(업링크 사망).
 *  · `last_dbfs` 바닥 = 프레임은 오는데 무음이다(마이크 사망). 클라 미터로는
 *    판정할 수 없다 — 고장나는 쪽이 클라다.
 *  · `t` 왕복 = 이 응답이 **내 ping** 에 대한 것임을 확인한다.
 */
export interface PongData {
  /** 클라가 보낸 t 의 에코(숫자가 아니었으면 null) */
  t: number | null
  /** 서버가 마지막으로 받은 오디오 프레임 seq. 없으면 null */
  ack: number | null
  /** 이 방이 받은 누적 프레임 수 */
  frames: number
  /** 서버가 **받은 바이트로 직접 잰** 마지막 프레임 dBFS */
  last_dbfs: number
  /** 이번 세션 최대 dBFS */
  peak_dbfs: number
  /** 마지막 프레임 이후 흐른 초(세션 없으면 null) */
  last_frame_age_s: number | null
}

/* ── 타입 가드 ───────────────────────────────────────────────────────────── */

export function isEnvelope(value: unknown): value is Envelope {
  if (typeof value !== 'object' || value === null) return false
  const e = value as Record<string, unknown>
  return (
    e['v'] === 1 &&
    typeof e['type'] === 'string' &&
    typeof e['room'] === 'string' &&
    typeof e['ts'] === 'number' &&
    (e['seq'] === null || typeof e['seq'] === 'number') &&
    typeof e['data'] === 'object' &&
    e['data'] !== null
  )
}

export function isType<T extends EventType>(
  envelope: Envelope,
  type: T,
): envelope is Envelope<T> {
  return envelope.type === type
}
